import type {
  GroupingRequest,
  RegressionAnalysisRequest,
  SurvivalAnalysisRequest,
  TableAnalysisRequest,
  WaterfallAnalysisRequest,
} from "@app/types/study";

/**
 * A study is its request: the cohorts, how they are grouped, and each analysis's settings. Nothing is
 * stored on the server — the page keeps the whole state in its URL, so a study is bookmarked, shared
 * with a colleague or reopened next week by its link, and the export carries the same definition.
 */

export type AnalysisTab = "table" | "survival" | "regression" | "waterfall";

export interface StudyState {
  cohorts: string[];
  grouping: GroupingRequest;
  tab: AnalysisTab | null;
  table: TableAnalysisRequest;
  survival: SurvivalAnalysisRequest;
  regression: RegressionAnalysisRequest | null;
  /** `null` until a score is chosen. */
  waterfall: WaterfallAnalysisRequest | null;
}

export const DEFAULT_TABLE: TableAnalysisRequest = {
  variables: [],
  mode: "auto",
  mcc: "bh",
  alpha: 0.05,
  absent: [],
};

export const DEFAULT_SURVIVAL: SurvivalAnalysisRequest = {
  event: "revision",
  horizons: [],
  include_revisions: false,
  alpha: 0.05,
};

export const DEFAULT_WATERFALL: Omit<WaterfallAnalysisRequest, "score"> = {
  horizon_months: 12,
  better: null,
  threshold: null,
  alpha: 0.05,
};

export function emptyState(): StudyState {
  return {
    cohorts: [],
    grouping: { kind: "none" },
    tab: null,
    table: { ...DEFAULT_TABLE },
    survival: { ...DEFAULT_SURVIVAL },
    regression: null,
    waterfall: null,
  };
}

function toBase64Url(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(encoded: string): string {
  const padded = encoded.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function encodeState(state: StudyState): string {
  return toBase64Url(JSON.stringify(state));
}

const TABS: readonly AnalysisTab[] = ["table", "survival", "regression", "waterfall"];

function asTab(value: string | null): AnalysisTab | null {
  return TABS.find((tab) => tab === value) ?? null;
}

/**
 * The state a URL describes. `?s=` holds a whole study; `?cohorts=a,b&tab=table` is the short form
 * the catalogue links with — « Comparer » opens the table, « Étudier » the list of questions. A link
 * that cannot be read starts an empty study rather than an error page: it is usually a link
 * truncated by a mail client.
 */
export function decodeState(params: URLSearchParams): StudyState {
  const base = emptyState();
  const encoded = params.get("s");
  if (encoded) {
    try {
      const parsed = JSON.parse(fromBase64Url(encoded)) as Partial<StudyState>;
      return {
        cohorts: Array.isArray(parsed.cohorts) ? parsed.cohorts.filter((c) => typeof c === "string") : [],
        grouping: parsed.grouping ?? base.grouping,
        tab: asTab(parsed.tab ?? null),
        table: { ...DEFAULT_TABLE, ...(parsed.table ?? {}) },
        survival: { ...DEFAULT_SURVIVAL, ...(parsed.survival ?? {}) },
        regression: parsed.regression ?? null,
        waterfall: parsed.waterfall ?? null,
      };
    } catch {
      return base;
    }
  }
  const cohorts = (params.get("cohorts") ?? "").split(",").filter(Boolean);
  return {
    ...base,
    cohorts,
    grouping: { kind: cohorts.length > 1 ? "cohorts" : "none" },
    tab: asTab(params.get("tab")),
  };
}

/** The most cohorts one study compares — `MAX_STUDY_COHORTS` on the server. */
export const MAX_STUDY_COHORTS = 6;

/** The catalogue's link to a study of these cohorts, opened on `tab` or on the list of questions. */
export function studyLink(cohortIds: string[], tab?: AnalysisTab): string {
  const cohorts = cohortIds.map(encodeURIComponent).join(",");
  return tab ? `/etudes?cohorts=${cohorts}&tab=${tab}` : `/etudes?cohorts=${cohorts}`;
}
